import React from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { formsApi } from "../../services/api";
import {
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "../../contexts/TranslationContext";
import LoadingSpinner from "../common/LoadingSpinner";
import ErrorAlert from "../common/ErrorAlert";

const FormList: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const {
    data: formsData,
    isLoading,
    error,
  } = useQuery("forms", () => formsApi.getForms(0, 100, "name", "asc", false));

  const forms = formsData?.items || [];

  const deleteFormMutation = useMutation(formsApi.deleteForm, {
    onSuccess: () => {
      queryClient.invalidateQueries("forms");
    },
  });

  const handleDelete = async (id: number) => {
    if (window.confirm(t("forms.detail.deleteConfirm"))) {
      await deleteFormMutation.mutateAsync(id);
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorAlert message={t("messages.networkError")} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            {t("forms.list.title")}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {t("forms.list.subtitle")}
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <Link
            to="/forms/new"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            {t("forms.list.createForm")}
          </Link>
        </div>
      </div>

      {/* Forms List */}
      {forms.length > 0 ? (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {forms.map((form) => (
              <li key={form.id}>
                <div className="px-4 py-4 flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center">
                        <DocumentDuplicateIcon className="h-6 w-6 text-primary-600" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="flex items-center">
                        <p className="text-sm font-medium text-gray-900">
                          <Link to={`/forms/${form.id}`}>{form.name}</Link>
                        </p>
                        <span
                          className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            form.is_active
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {form.is_active ? "Active" : "Inactive"}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center text-sm text-gray-500">
                        <span>
                          {form.variations.length} {t("forms.list.variations")}
                        </span>
                        <span className="mx-2">•</span>
                        <span>
                          {t("forms.list.created")}{" "}
                          {form.created_at
                            ? new Date(form.created_at).toLocaleDateString()
                            : "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {/* <button
                      onClick={() => copyEmbedCode(form.id!)}
                      className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <DocumentDuplicateIcon className="h-4 w-4 mr-1" />
                      {t('forms.list.embed')}
                    </button> */}
                    <Link
                      to={`/forms/${form.id}`}
                      className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <EyeIcon className="h-4 w-4 mr-1" />
                      {t("forms.list.view")}
                    </Link>
                    <Link
                      to={`/forms/${form.id}/edit`}
                      className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <PencilIcon className="h-4 w-4 mr-1" />
                      {t("forms.list.edit")}
                    </Link>
                    <button
                      onClick={() => handleDelete(form.id!)}
                      disabled={deleteFormMutation.isLoading}
                      className="inline-flex items-center px-3 py-1 border border-red-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                    >
                      <TrashIcon className="h-4 w-4 mr-1" />
                      {t("forms.list.delete")}
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="text-center py-12">
          <DocumentDuplicateIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {t("forms.list.noForms")}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {t("forms.list.noFormsDescription")}
          </p>
          <div className="mt-6">
            <Link
              to="/forms/new"
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              {t("forms.list.createFirstForm")}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormList;
